import trackUtil from '@/utils/analytics-service';
import { buildAnalyticsContext } from '@/hooks/useAnalyticsContext';
import {
  DataLoadCompletedProps,
  GraphCreatedProps,
  GraphDeletedProps,
  GraphScopedEventProps,
  QueryInstalledProps,
  QueryRunCompletedProps,
  SchemaSavedProps,
} from '@/utils/analytics/types';
import { WorkspaceT } from '@/pages/workgroup/type';

// The TigerGraph engine events. The tracking spec assigns these to Graph, Schema, Query
// and Loading "services", but those are the engine itself — the browser calls it directly
// on the workspace host and cloud-universe is not on that path, so nothing server-side can
// observe them. The legacy events these replace were portal-emitted too.

export type EngineEventSources = {
  userId?: string;
  organizationId?: string;
  workspace?: WorkspaceT | null;
  graphId?: string;
};

/**
 * Resolve the six identity fields every engine event requires.
 *
 * Returns null when any is missing rather than emitting a partial event: an event that
 * cannot be attributed to a workspace and graph is not usable for any of the funnels
 * these events exist to support.
 */
function resolveGraphScope(sources: EngineEventSources): GraphScopedEventProps | null {
  const context = buildAnalyticsContext(
    {},
    {
      userId: sources.userId,
      organizationId: sources.organizationId,
      workspace: sources.workspace,
      graphId: sources.graphId,
    }
  );

  const { user_id, organization_id, workgroup_id, workspace_id, database_id, graph_id } = context;
  if (!user_id || !organization_id || !workgroup_id || !database_id || !workspace_id || !graph_id) {
    return null;
  }

  return { user_id, organization_id, workgroup_id, workspace_id, database_id, graph_id };
}

export function trackGraphCreatedEvent(
  props: Omit<GraphCreatedProps, keyof GraphScopedEventProps>,
  sources: EngineEventSources
) {
  const scope = resolveGraphScope(sources);
  if (!scope) {
    return;
  }
  trackUtil.trackGraphCreated({ ...scope, ...props });
}

export function trackSchemaSavedEvent(
  props: Omit<SchemaSavedProps, keyof GraphScopedEventProps>,
  sources: EngineEventSources
) {
  const scope = resolveGraphScope(sources);
  if (!scope) {
    return;
  }
  trackUtil.trackSchemaSaved({ ...scope, ...props });
}

export function trackDataLoadCompletedEvent(
  props: Omit<DataLoadCompletedProps, keyof GraphScopedEventProps>,
  sources: EngineEventSources
) {
  const scope = resolveGraphScope(sources);
  if (!scope) {
    return;
  }
  trackUtil.trackDataLoadCompleted({ ...scope, ...props });
}

export function trackQueryInstalledEvent(
  props: Omit<QueryInstalledProps, keyof GraphScopedEventProps>,
  sources: EngineEventSources
) {
  const scope = resolveGraphScope(sources);
  if (!scope) {
    return;
  }
  trackUtil.trackQueryInstalled({ ...scope, ...props });
}

export function trackQueryRunCompletedEvent(
  props: Omit<QueryRunCompletedProps, keyof GraphScopedEventProps>,
  sources: EngineEventSources
) {
  const scope = resolveGraphScope(sources);
  if (!scope) {
    return;
  }
  trackUtil.trackQueryRunCompleted({ ...scope, ...props });
}

export function trackGraphDeletedEvent(
  props: Omit<GraphDeletedProps, keyof GraphScopedEventProps>,
  sources: EngineEventSources
) {
  const scope = resolveGraphScope(sources);
  if (!scope) {
    return;
  }
  trackUtil.trackGraphDeleted({ ...scope, ...props });
}
